import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardCanvas from "../components1/DashboardCanvas";
import uuid from "react-uuid";
import { Link } from "react-router-dom";
import useSheetDismiss from "../utils/useSheetDismiss";
import { addData, UpdateData, getData, deleteData } from "../service/APIService";
import "../styles/Todo.css";


const ToDoApp = () => {
  const [todos, setTodos] = useState([]);
  const [categories, setCategories] = useState(["Default"]);
  const [newTodo, setNewTodo] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Default");
  const [isImportant, setIsImportant] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { closing: addSheetClosing, requestClose: closeAddSheet } = useSheetDismiss(() => setShowModal(false));
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [editingTodo, setEditingTodo] = useState(null);
  const { closing: editSheetClosing, requestClose: closeEditSheet } = useSheetDismiss(() => setEditingTodo(null));

  // Quick inline add state
  const [quickText, setQuickText] = useState("");
  const [quickCategory, setQuickCategory] = useState("Default");

  // Filters
  const [selectedFilterCategory, setSelectedFilterCategory] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("PENDING"); // ALL | PENDING | COMPLETED
  const [searchQuery, setSearchQuery] = useState("");

  // Delete confirmation
  const [todoToDelete, setTodoToDelete] = useState(null);
  const { closing: deleteSheetClosing, requestClose: closeDeleteSheet } = useSheetDismiss(() => setTodoToDelete(null));

  // Notification / Toast with Undo
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
    const guestUser = JSON.parse(localStorage.getItem("guestUser"));
    if (sessionUser) {
      setIsLoggedIn(true);
      setEmail(sessionUser.email);
      loadTodosFromServer(sessionUser.email);
      loadCategoriesFromServer(sessionUser.email);
    } else {
      setIsLoggedIn(false);
      setEmail(guestUser?.email || "guest");
      loadTodosFromLocalStorage();
      loadCategoriesFromLocalStorage();
    }

    const handleOpenAddModal = () => setShowModal(true);
    window.addEventListener("open-add-todo", handleOpenAddModal);
  
  return () => window.removeEventListener("open-add-todo", handleOpenAddModal);
  }, []);

  const loadTodosFromLocalStorage = () => {
    const savedTodos = JSON.parse(localStorage.getItem("todos")) || [
      {
        id: "starter-1",
        text: "Review monthly shared budget",
        category: "Finance",
        completed: false,
        createddate: new Date().toISOString(),
        important: true,
        todotype: "personal",
      },
      {
        id: "starter-2",
        text: "Buy groceries & coffee beans",
        category: "Personal",
        completed: true,
        createddate: new Date(Date.now() - 86400000).toISOString(),
        important: false,
        todotype: "personal",
      },
    ];
    setTodos(savedTodos);
  };

  const loadCategoriesFromLocalStorage = () => {
    const savedCategories = JSON.parse(localStorage.getItem("todoCategories")) || [
      "Default",
      "Personal",
      "Finance",
      "Work",
    ];
    setCategories(savedCategories);
  };

  const loadTodosFromServer = async (userEmail) => {
    setLoading(true);
    try {
      const response = await getData(userEmail, "todo");
      setTodos(response || []);
    } catch (error) {
      console.error("Failed to load todos from server:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesFromServer = async (userEmail) => {
    try {
      const response = await getData(userEmail, "todocategory");
      setCategories(response?.map((item) => (typeof item === "object" ? item.name : item)) || ["Default"]);
    } catch (error) {
      console.error("Failed to load categories from server:", error);
    }
  };

  // Distinct sorted category names
  const categoryNames = useMemo(() => {
    const fromProps = categories.map((c) => (typeof c === "object" ? c.name : c));
    const fromTodos = todos.map((t) => t.category).filter(Boolean);
    const combined = Array.from(new Set(["Default", ...fromProps, ...fromTodos]));
    return combined.sort((a, b) => {
      if (a === "Archive") return 1;
      if (b === "Archive") return -1;
      return a.localeCompare(b);
    });
  }, [categories, todos]);

  // Statistics
  const stats = useMemo(() => {
    const nonArchived = todos.filter((t) => (t.category || "").toLowerCase() !== "archive" && !t.archived);
    const total = nonArchived.length;
    const completed = nonArchived.filter((t) => t.completed).length;
    const pending = total - completed;
    const important = nonArchived.filter((t) => t.important && !t.completed).length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, pending, important, percent };
  }, [todos]);

  // Filtered Todos
  const filteredTodos = useMemo(() => {
    return todos.filter((todo) => {
      const isArchived = (todo.category || "").toLowerCase() === "archive" || todo.archived === true;

      // Archive filter: ONLY show archived tasks
      if (selectedFilterCategory === "ARCHIVE") {
        if (!isArchived) return false;
      } else {
        // Any other filter (ALL, IMPORTANT, custom categories, etc.): NEVER show archived tasks
        if (isArchived) return false;

        // Category / Important filter
        if (selectedFilterCategory === "IMPORTANT") {
          if (!todo.important) return false;
        } else if (selectedFilterCategory !== "ALL") {
          if ((todo.category || "").toLowerCase() !== selectedFilterCategory.toLowerCase()) return false;
        }

        // Status filter applies only to non-archived views
        if (statusFilter === "PENDING" && todo.completed) return false;
        if (statusFilter === "COMPLETED" && !todo.completed) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesText = (todo.text || "").toLowerCase().includes(q);
        const matchesCat = (todo.category || "").toLowerCase().includes(q);
        if (!matchesText && !matchesCat) return false;
      }

      return true;
    }).sort((a, b) => {
      // Pending first, then important first, then newest
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      if (a.important !== b.important) return a.important ? -1 : 1;
      return new Date(b.createddate || 0) - new Date(a.createddate || 0);
    });
  }, [todos, selectedFilterCategory, statusFilter, searchQuery]);

  // Actions
  const handleQuickAdd = async (e) => {
    if (e) e.preventDefault();
    if (!quickText.trim()) {
      // If user clicks "+" without typing, open the Add Task modal with this category pre-selected
      setSelectedCategory(quickCategory || "Default");
      setShowModal(true);
      return;
    }

    const newTodoItem = {
      id: uuid(),
      text: quickText.trim(),
      category: quickCategory || "Default",
      completed: false,
      createddate: new Date().toISOString(),
      important: false,
      todotype: "personal",
    };

    saveTodoItem(newTodoItem);
    setQuickText("");
  };

  const handleModalAdd = async () => {
    if (!newTodo.trim()) return alert("Please enter task description");

    const newTodoItem = {
      id: uuid(),
      text: newTodo.trim(),
      category: selectedCategory || "Default",
      completed: false,
      createddate: new Date().toISOString(),
      important: isImportant,
      todotype: "personal",
    };

    saveTodoItem(newTodoItem);
    setNewTodo("");
    setIsImportant(false);
    closeAddSheet();
  };

  const saveTodoItem = async (newTodoItem) => {
    // 1. Immediately update UI state optimistically!
    const updatedTodos = [newTodoItem, ...todos];
    setTodos(updatedTodos);
    localStorage.setItem("todos", JSON.stringify(updatedTodos));

    // Ensure the new task is immediately visible in the active filter
    if (
      selectedFilterCategory !== "ALL" &&
      selectedFilterCategory.toLowerCase() !== newTodoItem.category?.toLowerCase()
    ) {
      setSelectedFilterCategory("ALL");
    }

    // 2. If logged in, sync to server in background
    if (isLoggedIn && email && email !== "guest") {
      try {
        await addData({ ...newTodoItem, type: "todo", email });
      } catch (error) {
        console.error("Failed to add todo to server:", error);
      }
    }
  };

  const showToast = (message, icon, onAction, actionLabel = "Undo") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const toastId = Date.now();
    setToast({ id: toastId, message, icon, onAction, actionLabel });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => (prev?.id === toastId ? null : prev));
    }, 5000);
  };

  const handleUndo = () => {
    if (toast && toast.onAction) {
      toast.onAction();
    }
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  };

  const toggleComplete = async (id) => {
    const targetTodo = todos.find((t) => t.id === id);
    if (!targetTodo) return;
    const newCompletedState = !targetTodo.completed;

    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, completed: newCompletedState } : todo
    );
    setTodos(updatedTodos);

    if (isLoggedIn) {
      try {
        const todoToUpdate = updatedTodos.find((todo) => todo.id === id);
        await UpdateData({ ...todoToUpdate, email });
      } catch (error) {
        console.error("Failed to update status on server:", error);
      }
    } else {
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }

    showToast(
      newCompletedState ? "Task marked completed" : "Task marked open",
      newCompletedState ? "fi-rr-check-circle" : "fi-rr-refresh",
      () => {
        setTodos((prev) => {
          const reverted = prev.map((t) =>
            t.id === id ? { ...t, completed: !newCompletedState } : t
          );
          if (!isLoggedIn) {
            localStorage.setItem("todos", JSON.stringify(reverted));
          } else {
            const todoToRevert = reverted.find((t) => t.id === id);
            if (todoToRevert) UpdateData({ ...todoToRevert, email });
          }
          return reverted;
        });
      }
    );
  };

  const toggleImportant = async (id) => {
    const targetTodo = todos.find((t) => t.id === id);
    if (!targetTodo) return;
    const newImportantState = !targetTodo.important;

    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, important: newImportantState } : todo
    );
    setTodos(updatedTodos);

    if (isLoggedIn) {
      try {
        const todoToUpdate = updatedTodos.find((todo) => todo.id === id);
        await UpdateData({ ...todoToUpdate, email });
      } catch (error) {
        console.error("Failed to update importance on server:", error);
      }
    } else {
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }

    showToast(
      newImportantState ? "Task starred" : "Task unstarred",
      newImportantState ? "fas fa-star" : "far fa-star",
      () => {
        setTodos((prev) => {
          const reverted = prev.map((t) =>
            t.id === id ? { ...t, important: !newImportantState } : t
          );
          if (!isLoggedIn) {
            localStorage.setItem("todos", JSON.stringify(reverted));
          } else {
            const todoToRevert = reverted.find((t) => t.id === id);
            if (todoToRevert) UpdateData({ ...todoToRevert, email });
          }
          return reverted;
        });
      }
    );
  };

  const executeDelete = async (id) => {
    const deletedTodo = todos.find((t) => t.id === id) || todoToDelete;
    if (!deletedTodo) return;

    if (isLoggedIn) {
      try {
        await deleteData(id);
      } catch (error) {
        console.error("Failed to delete todo:", error);
      }
    }

    const updatedTodos = todos.filter((todo) => todo.id !== id);
    setTodos(updatedTodos);
    if (!isLoggedIn) {
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }
    setTodoToDelete(null);

    const taskLabel =
      deletedTodo.text.length > 20
        ? deletedTodo.text.slice(0, 20) + "..."
        : deletedTodo.text;

    showToast(`"${taskLabel}" deleted`, "fi-rr-trash", async () => {
      setTodos((prev) => {
        const restored = [deletedTodo, ...prev];
        if (!isLoggedIn) {
          localStorage.setItem("todos", JSON.stringify(restored));
        }
        return restored;
      });
      if (isLoggedIn && email && email !== "guest") {
        try {
          await addData({ ...deletedTodo, type: "todo", email });
        } catch (e) {
          console.error("Failed to restore deleted todo:", e);
        }
      }
    });
  };

  const updateTodo = async () => {
    if (!editingTodo?.text?.trim()) return alert("Task text cannot be empty");

    if (isLoggedIn) {
      setLoading(true);
      try {
        await UpdateData({ ...editingTodo, email });
        loadTodosFromServer(email);
      } catch (error) {
        console.error("Failed to update todo:", error);
      } finally {
        setLoading(false);
      }
    } else {
      const updatedTodos = todos.map((todo) =>
        todo.id === editingTodo.id ? editingTodo : todo
      );
      setTodos(updatedTodos);
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }
    setEditingTodo(null);
  };

  const archiveTodo = async (id) => {
    const targetTodo = todos.find((t) => t.id === id);
    if (!targetTodo) return;
    const previousCategory = targetTodo.category || "Default";

    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, category: "Archive" } : todo
    );
    setTodos(updatedTodos);

    if (isLoggedIn) {
      try {
        const todoToArchive = updatedTodos.find((todo) => todo.id === id);
        await UpdateData({ ...todoToArchive, email });
      } catch (error) {
        console.error("Failed to archive todo:", error);
      }
    } else {
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }

    showToast("Task moved to Archive", "fi-rr-box-alt", () => {
      setTodos((prev) => {
        const reverted = prev.map((t) =>
          t.id === id ? { ...t, category: previousCategory } : t
        );
        if (!isLoggedIn) {
          localStorage.setItem("todos", JSON.stringify(reverted));
        } else {
          const todoToRevert = reverted.find((t) => t.id === id);
          if (todoToRevert) UpdateData({ ...todoToRevert, email });
        }
        return reverted;
      });
    });
  };

  const unarchiveTodo = async (id) => {
    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, category: "Default" } : todo
    );
    setTodos(updatedTodos);

    if (isLoggedIn) {
      try {
        const todoToRestore = updatedTodos.find((todo) => todo.id === id);
        await UpdateData({ ...todoToRestore, email });
      } catch (error) {
        console.error("Failed to restore todo from archive:", error);
      }
    } else {
      localStorage.setItem("todos", JSON.stringify(updatedTodos));
    }

    showToast("Task restored to active list", "fi-rr-inbox-in");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    return `${day} ${month}`;
  };

  return (
    <div className="todo-page-wrapper">
      <DashboardCanvas theme="todo" />
      {/* Hero Focus & Progress Card */}
      <div className="todo-hero-card">
        <div className="todo-hero-header">
          <div className="todo-hero-title-group">
            <h2>Tasks & Focus</h2>
          </div>
          <button
            className="todo-hero-add-btn"
            onClick={() => setShowModal(true)}
            title="Create Task"
          >
            <i className="fi fi-rr-plus"></i>
          </button>
        </div>

        {/* Progress Bar Section */}
        <div className="todo-progress-section">
          <div className="todo-progress-label-row">
            <span>Overall Progress</span>
            <span>{stats.percent}% Done</span>
          </div>
          <div className="todo-progress-track">
            <div
              className="todo-progress-fill"
              style={{ width: `${stats.percent}%` }}
            ></div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="todo-stats-grid">
          <div className="todo-stat-box">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-text">Total</span>
          </div>
          <div className="todo-stat-box">
            <span className="stat-number" style={{ color: "#EF8767" }}>
              {stats.pending}
            </span>
            <span className="stat-text">Pending</span>
          </div>
          <div className="todo-stat-box">
            <span className="stat-number" style={{ color: "#34D399" }}>
              {stats.completed}
            </span>
            <span className="stat-text">Done</span>
          </div>
          <div className="todo-stat-box">
            <span className="stat-number" style={{ color: "#FBBF24" }}>
              {stats.important}
            </span>
            <span className="stat-text">Starred</span>
          </div>
        </div>
      </div>

      {/* Quick Add Bar */}
      <form onSubmit={handleQuickAdd} className="todo-quick-add-wrap">
        <i className="fi fi-rr-check-circle" style={{ color: "var(--accent-mauve)" }}></i>
        <input
          type="text"
          className="todo-quick-input"
          placeholder="Add a new task... (or tap +)"
          value={quickText}
          onChange={(e) => setQuickText(e.target.value)}
        />
        <select
          className="todo-quick-category-select"
          value={quickCategory}
          onChange={(e) => {
            const cat = e.target.value;
            setQuickCategory(cat);
            setSelectedCategory(cat);
          }}
          title="Select task category"
        >
          {categoryNames.filter((c) => c !== "Archive").map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="todo-quick-btn"
          title={quickText.trim() ? "Add task" : "Open task dialog"}
        >
          <i className="fi fi-rr-plus"></i>
        </button>
      </form>

      {/* Filters: Search & Status Segmented Switch */}
      <div className="todo-filter-row">
        <div className="todo-search-box">
          <i className="fi fi-rr-search"></i>
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="border-0 bg-transparent p-0 text-muted"
              onClick={() => setSearchQuery("")}
            >
              <i className="fi fi-rr-cross-small"></i>
            </button>
          )}
        </div>

        <div className="todo-segmented-status">
          <button
            type="button"
            className={`todo-segmented-btn ${statusFilter === "ALL" ? "active" : ""}`}
            onClick={() => setStatusFilter("ALL")}
          >
            All
          </button>
          <button
            type="button"
            className={`todo-segmented-btn ${statusFilter === "PENDING" ? "active" : ""}`}
            onClick={() => setStatusFilter("PENDING")}
          >
            Open
          </button>
          <button
            type="button"
            className={`todo-segmented-btn ${statusFilter === "COMPLETED" ? "active" : ""}`}
            onClick={() => setStatusFilter("COMPLETED")}
          >
            Done
          </button>
        </div>
      </div>

      {/* Category Filter Pills Bar */}
      <div className="todo-category-pills-bar">
        <button
          type="button"
          className={`todo-cat-pill ${selectedFilterCategory === "ALL" ? "active" : ""}`}
          onClick={() => setSelectedFilterCategory("ALL")}
        >
          <span>All Tasks</span>
          <span className="pill-badge">{stats.total}</span>
        </button>

        <button
          type="button"
          className={`todo-cat-pill ${selectedFilterCategory === "IMPORTANT" ? "active" : ""}`}
          onClick={() => setSelectedFilterCategory("IMPORTANT")}
        >
          <i className="fas fa-star" style={{ color: "#F59E0B" }}></i>
          <span>Important</span>
          <span className="pill-badge">{stats.important}</span>
        </button>

        {categoryNames
          .filter((cat) => cat !== "Archive")
          .map((cat) => {
            const count = todos.filter((t) => t.category?.toLowerCase() === cat.toLowerCase()).length;
            return (
              <button
                key={cat}
                type="button"
                className={`todo-cat-pill ${selectedFilterCategory === cat ? "active" : ""}`}
                onClick={() => setSelectedFilterCategory(cat)}
              >
                <span>{cat}</span>
                <span className="pill-badge">{count}</span>
              </button>
            );
          })}

        <button
          type="button"
          className={`todo-cat-pill ${selectedFilterCategory === "ARCHIVE" ? "active" : ""}`}
          onClick={() => setSelectedFilterCategory("ARCHIVE")}
        >
          <i className="fi fi-rr-box-alt"></i>
          <span>Archive</span>
          <span className="pill-badge">
            {todos.filter((t) => t.category === "Archive").length}
          </span>
        </button>

        <Link to="/app1/settings" className="todo-cat-pill" title="Manage categories">
          <i className="fi fi-rr-settings"></i>
          <span>Manage</span>
        </Link>
      </div>

      {/* Todo List Items */}
      {loading ? (
        <div className="text-center py-5">
          <i className="fi fi-rr-spinner fa-spin" style={{ fontSize: "28px", color: "var(--accent-mauve)" }}></i>
          <p className="mt-2 text-muted" style={{ fontSize: "13px" }}>Loading tasks...</p>
        </div>
      ) : filteredTodos.length === 0 ? (
        <div className="todo-empty-state">
          <div className="todo-empty-icon">
            <i className="fi fi-rr-check"></i>
          </div>
          <h4>No tasks found</h4>
          <p>
            {searchQuery
              ? "No tasks match your search filter."
              : selectedFilterCategory === "ARCHIVE"
              ? "No archived tasks."
              : "All caught up! Tap + to add your next task."}
          </p>
          <button
            type="button"
            className="btn-coral-modern mx-auto"
            style={{ maxWidth: "180px", padding: "10px 16px", fontSize: "13.5px" }}
            onClick={() => setShowModal(true)}
          >
            <i className="fi fi-rr-plus"></i> New Task
          </button>
        </div>
      ) : (
        <div className="todo-list-container">
          {filteredTodos.map((todo) => {
            const isDone = !!todo.completed;
            const isStar = !!todo.important;
            return (
              <div
                key={todo.id}
                className={`todo-card ${isDone ? "completed" : ""} ${isStar ? "is-important" : ""}`}
              >
                {/* Circular Checkbox */}
                <button
                  type="button"
                  className={`todo-check-btn ${isDone ? "checked" : ""}`}
                  onClick={() => toggleComplete(todo.id)}
                  title={isDone ? "Mark Pending" : "Mark Completed"}
                >
                  <i className="fi fi-rr-check"></i>
                </button>

                {/* Content */}
                <div className="todo-body" onClick={() => setEditingTodo({ ...todo })}>
                  <div className={`todo-text ${isDone ? "completed-text" : ""}`}>
                    {todo.text}
                  </div>
                  <div className="todo-meta-row">
                    <span className="todo-category-badge">{todo.category || "Default"}</span>
                    {todo.createddate && (
                      <span className="todo-date-badge">
                        <i className="fi fi-rr-calendar"></i>
                        {formatDate(todo.createddate)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right Action Buttons */}
                <div className="todo-actions-wrap">
                  {/* Star Toggle */}
                  <button
                    type="button"
                    className={`todo-action-icon-btn ${isStar ? "star-active" : ""}`}
                    onClick={() => toggleImportant(todo.id)}
                    title={isStar ? "Unstar" : "Star"}
                  >
                    <i className={isStar ? "fas fa-star" : "far fa-star"}></i>
                  </button>

                  {/* Edit */}
                  <button
                    type="button"
                    className="todo-action-icon-btn"
                    onClick={() => setEditingTodo({ ...todo })}
                    title="Edit Task"
                  >
                    <i className="fi fi-rr-pencil"></i>
                  </button>

                  {/* Archive or Restore */}
                  {(todo.category || "").toLowerCase() === "archive" || todo.archived ? (
                    <button
                      type="button"
                      className="todo-action-icon-btn restore"
                      onClick={() => unarchiveTodo(todo.id)}
                      title="Restore Task"
                    >
                      <i className="fi fi-rr-inbox-in"></i>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="todo-action-icon-btn"
                      onClick={() => archiveTodo(todo.id)}
                      title="Archive Task"
                    >
                      <i className="fi fi-rr-box-alt"></i>
                    </button>
                  )}

                  {/* Delete */}
                  <button
                    type="button"
                    className="todo-action-icon-btn trash"
                    onClick={() => setTodoToDelete(todo)}
                    title="Delete Task"
                  >
                    <i className="fi fi-rr-trash"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Slide-Up Bottom Sheet: Add Task Modal */}
      {showModal && (
        <div className={"bottom-sheet-overlay"} onClick={closeAddSheet}>
          <div className={`bottom-sheet-content${addSheetClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeAddSheet}></div>
            <button
              type="button"
              className="bottom-sheet-close-corner"
              onClick={closeAddSheet}
              aria-label="Close"
              title="Close"
            >
              <i className="fi fi-rr-cross"></i>
            </button>
            <div className="mb-3">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)", fontSize: "17px" }}>
                Add New Task
              </h5>
            </div>

            <div className="modern-input-group mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label>Task Description</label>
                <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                  {120 - newTodo.length} left
                </span>
              </div>
              <textarea
                rows={2}
                className="modern-input"
                placeholder="What needs to be done?"
                value={newTodo}
                maxLength={120}
                onChange={(e) => setNewTodo(e.target.value)}
                autoFocus
              />
            </div>

            {/* Category Selector */}
            <div className="modern-input-group mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="mb-0">Category</label>
                <Link
                  to="/app1/settings"
                  className="todo-manage-categories-link"
                  onClick={() => setShowModal(false)}
                >
                  <i className="fi fi-rr-plus-small"></i> Manage
                </Link>
              </div>

              <div className="category-2col-grid">
                {categoryNames
                  .filter((c) => c !== "Archive")
                  .map((cat, idx) => {
                    const isSelected = selectedCategory.toLowerCase() === cat.toLowerCase();
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`category-2col-btn ${isSelected ? "active" : ""}`}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        <i className="fi fi-rr-bookmark"></i>
                        <span>{cat}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Importance Switch */}
            <div className="card-modern p-3 mb-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-star" style={{ color: "#F59E0B", fontSize: "18px" }}></i>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--primary-dark)" }}>
                    High Priority Task
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                    Keep this task pinned at the top
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                className="form-check-input"
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
            </div>

            <button
              type="button"
              className="btn-coral-modern"
              onClick={handleModalAdd}
              disabled={loading || !newTodo.trim()}
            >
              <i className="fi fi-rr-check"></i>
              <span>{loading ? "Adding..." : "Save Task"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Slide-Up Bottom Sheet: Edit Task Modal */}
      {editingTodo && (
        <div className="bottom-sheet-overlay" onClick={closeEditSheet}>
          <div className={`bottom-sheet-content${editSheetClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeEditSheet}></div>
            <button
              type="button"
              className="bottom-sheet-close-corner"
              onClick={closeEditSheet}
              aria-label="Close"
              title="Close"
            >
              <i className="fi fi-rr-cross"></i>
            </button>
            <div className="mb-3">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)", fontSize: "17px" }}>
                Edit Task
              </h5>
            </div>

            <div className="modern-input-group mb-3">
              <label>Task Description</label>
              <textarea
                rows={2}
                className="modern-input"
                value={editingTodo.text || ""}
                onChange={(e) =>
                  setEditingTodo((prev) => ({ ...prev, text: e.target.value }))
                }
              />
            </div>

            <div className="modern-input-group mb-3">
              <label>Category</label>
              <div className="category-2col-grid">
                {categoryNames
                  .filter((c) => c !== "Archive")
                  .map((cat, idx) => {
                    const isSelected =
                      (editingTodo.category || "Default").toLowerCase() === cat.toLowerCase();
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`category-2col-btn ${isSelected ? "active" : ""}`}
                        onClick={() =>
                          setEditingTodo((prev) => ({ ...prev, category: cat }))
                        }
                      >
                        <i className="fi fi-rr-bookmark"></i>
                        <span>{cat}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="card-modern p-3 mb-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-star" style={{ color: "#F59E0B", fontSize: "18px" }}></i>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--primary-dark)" }}>
                    High Priority Task
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                    Pin this task as important
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                className="form-check-input"
                style={{ width: "20px", height: "20px", cursor: "pointer" }}
                checked={!!editingTodo.important}
                onChange={(e) =>
                  setEditingTodo((prev) => ({ ...prev, important: e.target.checked }))
                }
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn-primary-modern flex-1"
                onClick={updateTodo}
                disabled={loading || !editingTodo.text?.trim()}
              >
                <i className="fi fi-rr-check"></i>
                <span>{loading ? "Saving..." : "Update Task"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {todoToDelete && (
        <div className="bottom-sheet-overlay" onClick={closeDeleteSheet}>
          <div className={`bottom-sheet-content${deleteSheetClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeDeleteSheet}></div>
            <button
              type="button"
              className="bottom-sheet-close-corner"
              onClick={closeDeleteSheet}
              aria-label="Close"
              title="Close"
            >
              <i className="fi fi-rr-cross"></i>
            </button>
            <div className="text-center py-2">
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  background: "#FEE2E2",
                  color: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  margin: "0 auto 12px auto",
                }}
              >
                <i className="fi fi-rr-trash"></i>
              </div>
              <h4 style={{ fontSize: "17px", fontWeight: 700, color: "var(--primary-dark)" }}>
                Delete Task?
              </h4>
              <p style={{ fontSize: "13px", color: "var(--text-muted)", maxWidth: "280px", margin: "0 auto 20px auto" }}>
                "{todoToDelete.text}" will be permanently removed.
              </p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light flex-1 py-2"
                  style={{ borderRadius: "12px", fontWeight: 600, border: "1px solid var(--border-subtle)" }}
                  onClick={closeDeleteSheet}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger flex-1 py-2"
                  style={{ borderRadius: "12px", fontWeight: 600 }}
                  onClick={() => executeDelete(todoToDelete.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification with Undo */}
      {toast && (
        <div className="todo-toast-container" role="status" aria-live="polite">
          <div className="todo-toast-card">
            <div className="todo-toast-left">
              <span className="todo-toast-icon">
                <i className={`fi ${toast.icon || "fi-rr-bell"}`}></i>
              </span>
              <span className="todo-toast-msg">{toast.message}</span>
            </div>
            <div className="todo-toast-right">
              {toast.onAction && (
                <button
                  type="button"
                  className="todo-toast-undo-btn"
                  onClick={handleUndo}
                >
                  {toast.actionLabel || "Undo"}
                </button>
              )}
              <button
                type="button"
                className="todo-toast-close-btn"
                onClick={() => setToast(null)}
                aria-label="Dismiss notification"
              >
                <i className="fi fi-rr-cross-small"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToDoApp;
